import { cloneDeep } from 'lodash-es'
import { asyncRouterMap } from '@/router'
import { pathResolve } from '@/utils/routerHelper'

/** 仅 super_admin 可在后台看见的页面（配置页本身） */
export const ROUTES_SUPER_ADMIN_ONLY = new Set<string>(['AdminMenuAccess'])

const DEFAULT_STAFF_ROLES_MENU = ['admin', 'super_admin', 'ops_admin'] as const

export function userMayAccessRoute(
  name: string | undefined,
  userRoles: string[],
  grants: Record<string, string[]>
): boolean {
  if (!name) return false
  if (ROUTES_SUPER_ADMIN_ONLY.has(name)) {
    return userRoles.includes('super_admin')
  }
  if (userRoles.includes('super_admin')) return true

  const g = grants[name]
  let allowed: readonly string[]
  if (g === undefined) {
    allowed = DEFAULT_STAFF_ROLES_MENU
  } else if (g.length === 0) {
    return false
  } else {
    allowed = g
  }
  return userRoles.some((r) => allowed.includes(r))
}

export function filterAsyncRoutesByMenuAccess(
  routes: AppRouteRecordRaw[],
  userRoles: string[],
  grants: Record<string, string[]>
): AppRouteRecordRaw[] {
  const res: AppRouteRecordRaw[] = []
  for (const route of routes) {
    const meta = route.meta ?? {}
    if (meta.hidden && !meta.canTo) continue

    const node = { ...route } as AppRouteRecordRaw
    if (node.children?.length) {
      node.children = filterAsyncRoutesByMenuAccess(node.children, userRoles, grants)
      if (node.children.length === 0) continue
    } else if (!userMayAccessRoute(node.name, userRoles, grants)) {
      continue
    }
    res.push(node)
  }
  return res
}

export function collectAdminMenuRouteDescriptors(
  routes: AppRouteRecordRaw[] = asyncRouterMap,
  parentSection = ''
): { name: string; title: string; parentSection: string }[] {
  const out: { name: string; title: string; parentSection: string }[] = []
  for (const route of routes) {
    const meta = route.meta ?? {}
    if (meta.hidden && !meta.canTo) continue

    if (route.children?.length) {
      const section =
        String(meta.title || '').trim() || String(route.name || '').trim() || parentSection || '—'
      out.push(...collectAdminMenuRouteDescriptors(route.children, section))
    } else {
      const title = String(meta.title || route.name || '').trim() || route.name
      out.push({
        name: route.name,
        title,
        parentSection: parentSection || '—'
      })
    }
  }
  return out
}

/** 第一个可访问的完整路径（不含 404 等），用于登录后默认跳转 */
export function firstAccessibleMenuPath(
  routes: AppRouteRecordRaw[],
  userRoles: string[],
  grants: Record<string, string[]>,
  basePath = ''
): string | null {
  for (const route of routes) {
    const meta = route.meta ?? {}
    if (meta.hidden && !meta.canTo) continue
    if (route.name === '404Page') continue

    const full = pathResolve(basePath, route.path)
    if (route.children?.length) {
      const sub = firstAccessibleMenuPath(route.children, userRoles, grants, full)
      if (sub) return sub
    } else if (userMayAccessRoute(route.name, userRoles, grants)) {
      return full.startsWith('/') ? full : `/${full}`
    }
  }
  return null
}

export function buildFilteredAdminRouterMap(
  userRoles: string[],
  grants: Record<string, string[]>
): AppRouteRecordRaw[] {
  return filterAsyncRoutesByMenuAccess(cloneDeep(asyncRouterMap), userRoles, grants)
}
